import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import KpiPreferencesService from '@/services/KpiPreferencesService';
import type {
  AssetKpiPreference,
  CannedKpi,
  CreateKpiPreferenceRequest,
  UpdateKpiPreferenceRequest,
  UserKpiPreference,
} from '@/types/kpi';
import {
  BarChart3,
  Building,
  Calculator,
  Eye,
  EyeOff,
  Plus,
  Settings,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

export default function KpiSettingsPage() {
  const [dashboardPreferences, setDashboardPreferences] = useState<
    UserKpiPreference[]
  >([]);
  const [assetPreferences, setAssetPreferences] = useState<
    AssetKpiPreference[]
  >([]);
  const [cannedKpis, setCannedKpis] = useState<CannedKpi[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedScope, setSelectedScope] = useState<'dashboard' | 'asset'>(
    'dashboard',
  );
  const [selectedKpi, setSelectedKpi] = useState<CannedKpi | null>(null);
  const [customDisplayName, setCustomDisplayName] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashboardPrefs, assetPrefs, kpis] = await Promise.all([
        KpiPreferencesService.getUserKpiPreferences('dashboard'),
        KpiPreferencesService.getUserKpiPreferences('asset'),
        KpiPreferencesService.getCannedKpis(),
      ]);

      setDashboardPreferences(dashboardPrefs);
      setAssetPreferences(assetPrefs);
      setCannedKpis(kpis);
    } catch (error) {
      console.error('Error loading KPI settings:', error);
      toast.error('Failed to load KPI settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddKpi = async () => {
    if (!selectedKpi) return;

    try {
      const preference: CreateKpiPreferenceRequest = {
        scope: selectedScope,
        kpi_id: selectedKpi.name,
        kpi_type: 'canned',
        display_name: customDisplayName || selectedKpi.display_name,
        description: selectedKpi.description,
        configuration: {
          period: 'annual',
        },
        is_enabled: isEnabled,
      };

      await KpiPreferencesService.createUserKpiPreference(preference);

      toast.success('KPI preference added successfully');
      setIsAddDialogOpen(false);
      setSelectedKpi(null);
      setCustomDisplayName('');
      setIsEnabled(true);
      loadData();
    } catch (error) {
      console.error('Error adding KPI preference:', error);
      toast.error('Failed to add KPI preference');
    }
  };

  const handleToggleKpi = async (
    preference: UserKpiPreference | AssetKpiPreference,
  ) => {
    try {
      const updates: UpdateKpiPreferenceRequest = {
        is_enabled: !preference.is_enabled,
      };

      if ('asset_id' in preference) {
        await KpiPreferencesService.updateAssetKpiPreference(
          preference.id,
          updates,
        );
      } else {
        await KpiPreferencesService.updateUserKpiPreference(
          preference.id,
          updates,
        );
      }

      toast.success(
        `KPI ${preference.is_enabled ? 'disabled' : 'enabled'} successfully`,
      );
      loadData();
    } catch (error) {
      console.error('Error toggling KPI:', error);
      toast.error('Failed to update KPI preference');
    }
  };

  const handleDeleteKpi = async (
    preference: UserKpiPreference | AssetKpiPreference,
  ) => {
    try {
      if ('asset_id' in preference) {
        await KpiPreferencesService.deleteAssetKpiPreference(preference.id);
      } else {
        await KpiPreferencesService.deleteUserKpiPreference(preference.id);
      }

      toast.success('KPI preference removed successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting KPI preference:', error);
      toast.error('Failed to remove KPI preference');
    }
  };

  const getKpiCategory = (kpiId: string) => {
    const cannedKpi = cannedKpis.find((ck) => ck.name === kpiId);
    return cannedKpi?.category || 'unknown';
  };

  const getKpiDescription = (kpiId: string) => {
    const cannedKpi = cannedKpis.find((ck) => ck.name === kpiId);
    return cannedKpi?.description || '';
  };

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">KPI Settings</h1>
        <p className="text-muted-foreground">
          Customize which KPIs you want to track on your dashboard and
          individual assets
        </p>
      </div>

      <Tabs defaultValue="dashboard" className="space-y-6">
        <TabsList>
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Dashboard KPIs
          </TabsTrigger>
          <TabsTrigger value="asset" className="flex items-center gap-2">
            <Building className="h-4 w-4" />
            Asset KPIs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Dashboard KPIs</h2>
              <p className="text-sm text-muted-foreground">
                KPIs that appear on your main dashboard
              </p>
            </div>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setSelectedScope('dashboard')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Dashboard KPI
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Add Dashboard KPI</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="kpi-select">Select KPI</Label>
                    <Select
                      onValueChange={(value) => {
                        const kpi = cannedKpis.find((k) => k.name === value);
                        setSelectedKpi(kpi || null);
                        setCustomDisplayName(kpi?.display_name || '');
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a KPI" />
                      </SelectTrigger>
                      <SelectContent>
                        {cannedKpis.map((kpi) => (
                          <SelectItem key={kpi.name} value={kpi.name}>
                            <div className="flex items-center gap-2">
                              <span>{kpi.display_name}</span>
                              <Badge variant="secondary" className="text-xs">
                                {kpi.category}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedKpi && (
                    <div>
                      <Label htmlFor="display-name">Display Name</Label>
                      <Input
                        id="display-name"
                        value={customDisplayName}
                        onChange={(e) => setCustomDisplayName(e.target.value)}
                        placeholder={selectedKpi.display_name}
                      />
                      {selectedKpi.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {selectedKpi.description}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="enabled"
                      checked={isEnabled}
                      onCheckedChange={setIsEnabled}
                    />
                    <Label htmlFor="enabled">Enable this KPI</Label>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setIsAddDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleAddKpi} disabled={!selectedKpi}>
                      Add KPI
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {dashboardPreferences.length === 0 ? (
              <Card className="p-8 text-center">
                <div className="flex flex-col items-center gap-4">
                  <Calculator className="h-12 w-12 text-muted-foreground" />
                  <div>
                    <h3 className="text-lg font-semibold">No Dashboard KPIs</h3>
                    <p className="text-muted-foreground">
                      Add KPIs to track your portfolio performance
                    </p>
                  </div>
                </div>
              </Card>
            ) : (
              dashboardPreferences.map((preference) => (
                <Card key={preference.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium">
                            {preference.display_name}
                          </h3>
                          <Badge variant="outline" className="text-xs">
                            {getKpiCategory(preference.kpi_id)}
                          </Badge>
                          {!preference.is_enabled && (
                            <Badge variant="secondary" className="text-xs">
                              Disabled
                            </Badge>
                          )}
                        </div>
                        {getKpiDescription(preference.kpi_id) && (
                          <p className="text-sm text-muted-foreground">
                            {getKpiDescription(preference.kpi_id)}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleKpi(preference)}
                        >
                          {preference.is_enabled ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteKpi(preference)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="asset" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Asset KPIs</h2>
              <p className="text-sm text-muted-foreground">
                KPIs that appear on individual asset pages
              </p>
            </div>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setSelectedScope('asset')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Asset KPI
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Add Asset KPI</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="kpi-select">Select KPI</Label>
                    <Select
                      onValueChange={(value) => {
                        const kpi = cannedKpis.find((k) => k.name === value);
                        setSelectedKpi(kpi || null);
                        setCustomDisplayName(kpi?.display_name || '');
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a KPI" />
                      </SelectTrigger>
                      <SelectContent>
                        {cannedKpis.map((kpi) => (
                          <SelectItem key={kpi.name} value={kpi.name}>
                            <div className="flex items-center gap-2">
                              <span>{kpi.display_name}</span>
                              <Badge variant="secondary" className="text-xs">
                                {kpi.category}
                              </Badge>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedKpi && (
                    <div>
                      <Label htmlFor="display-name">Display Name</Label>
                      <Input
                        id="display-name"
                        value={customDisplayName}
                        onChange={(e) => setCustomDisplayName(e.target.value)}
                        placeholder={selectedKpi.display_name}
                      />
                      {selectedKpi.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {selectedKpi.description}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="enabled"
                      checked={isEnabled}
                      onCheckedChange={setIsEnabled}
                    />
                    <Label htmlFor="enabled">Enable this KPI</Label>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setIsAddDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleAddKpi} disabled={!selectedKpi}>
                      Add KPI
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {assetPreferences.length === 0 ? (
              <Card className="p-8 text-center">
                <div className="flex flex-col items-center gap-4">
                  <Calculator className="h-12 w-12 text-muted-foreground" />
                  <div>
                    <h3 className="text-lg font-semibold">No Asset KPIs</h3>
                    <p className="text-muted-foreground">
                      Add KPIs to track individual property performance
                    </p>
                  </div>
                </div>
              </Card>
            ) : (
              assetPreferences.map((preference) => (
                <Card key={preference.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium">
                            {preference.display_name}
                          </h3>
                          <Badge variant="outline" className="text-xs">
                            {getKpiCategory(preference.kpi_id)}
                          </Badge>
                          {!preference.is_enabled && (
                            <Badge variant="secondary" className="text-xs">
                              Disabled
                            </Badge>
                          )}
                        </div>
                        {getKpiDescription(preference.kpi_id) && (
                          <p className="text-sm text-muted-foreground">
                            {getKpiDescription(preference.kpi_id)}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleKpi(preference)}
                        >
                          {preference.is_enabled ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteKpi(preference)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
